import React, { memo } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { Grid, FormHelperText } from "@mui/material";
import { PosSoftware, Data } from "../../../store/possoftware/types";
import { saveRequest as savePosSoftware } from "../../../store/possoftware/actions";
import { Formik, Form, Field, FormikProps, FieldProps } from "formik";
import ArrayInput from "../../../common/arrayInput/ArrayInput";
import pageStyles from "../../pageStyles";
import FormSubmitButtons from "../../../common/buttons/FormSubmitButtons";
import SectionWrapper from "../../../common/pageSections/SectionWrapper";
import schema from "./posSoftwareEditForm.schema";
import TextInput from "../../../common/formElements/TextInput";
import { useToastMessage } from "../../../effects/useToastMessage";
import { toast } from "react-toastify";

interface PosSoftwareEditForm {
  posSoftware?: Data;
  isCreate: boolean;
}

function PosSoftwareEditForm({ posSoftware, isCreate }: PosSoftwareEditForm) {
  const dispatch = useDispatch();
  const history = useHistory();
  const classes = pageStyles();

  const initialValues = posSoftware || {
    id: null,
    type: "PosSoftware",
    attributes: {
      ProviderIdentification: "",
      ApplicationName: "",
      SoftwareVersion: [],
      CertificationCode: "",
      KEK: "",
      KEKIdentifier: "",
      KEKVersion: "",
    },
  };

  return (
    <Formik
      validationSchema={schema}
      initialValues={initialValues}
      onSubmit={(values) => {
        const callback = (err: string | null, _data: PosSoftware | null) => {
          if (err) {
            console.log(err);
            let errText = `An error occurred while saving: ${err}`;
            toast.error(errText, {
              closeOnClick: true,
            });
          } else {
            if (isCreate) {
              history.goBack();
            } else {
              toast.success("Saved successfully!", {
                closeOnClick: true,
              });
            }
          }
        };
        dispatch(savePosSoftware(values, callback));
      }}
    >
      {(props: FormikProps<Data>) => {
        const { errors, values, isSubmitting } = props;

        // Show toast errors
        useToastMessage(isSubmitting, errors);
        return (
          <Form>
            <SectionWrapper title="POS Software">
              <SectionWrapper title="Info" isChild>
                <Grid
                  container
                  alignItems="flex-start"
                  spacing={2}
                  className={classes.formWrapper as string}
                >
                  <Grid item xs={12} md={4}>
                    <TextInput
                      fieldname="attributes.ProviderIdentification"
                      label="Provider Identification*"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextInput
                      fieldname="attributes.ApplicationName"
                      label="Application name"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextInput
                      fieldname="attributes.CertificationCode"
                      label="Certification code"
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextInput fieldname="attributes.KEK" label="KEK" />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextInput
                      fieldname="attributes.KEKIdentifier"
                      label="KEK Identifier"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextInput
                      fieldname="attributes.KEKVersion"
                      label="KEK Version"
                    />
                  </Grid>
                  <FormSubmitButtons />
                </Grid>
              </SectionWrapper>

              <SectionWrapper title="Software Version" isChild>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Field name="attributes.POIID">
                      {(props: FieldProps) => {
                        const {
                          form: { setFieldValue },
                        } = props;
                        return (
                          <ArrayInput
                            label="Software Versions:"
                            value={values.attributes.SoftwareVersion || []}
                            onChange={(data: string[]) => {
                              setFieldValue("attributes.SoftwareVersion", data);
                            }}
                          />
                        );
                      }}
                    </Field>
                    {errors?.attributes?.SoftwareVersion && (
                      <FormHelperText error>
                        {errors?.attributes?.SoftwareVersion}
                      </FormHelperText>
                    )}
                  </Grid>
                </Grid>
              </SectionWrapper>
            </SectionWrapper>
          </Form>
        );
      }}
    </Formik>
  );
}
export default memo(PosSoftwareEditForm);
